import React, { useState } from 'react';
import {
  makeStyles,
  tokens,
  Text,
  Button,
  mergeClasses,
  Tooltip,
} from '@fluentui/react-components';
import {
  Home24Regular,
  Home24Filled,
  Clock24Regular,
  Pin24Regular,
  Navigation24Regular,
  People24Regular,
  Flow24Regular,
  SignOut24Regular,
  ChevronDown20Regular,
  ChevronUp20Regular,
  LineHorizontal320Regular,
  Settings24Regular,
} from '@fluentui/react-icons';
import { ViewMode } from '../services/configService';
import { AdminSettingsModal } from './AdminSettingsModal';

const useStyles = makeStyles({
  layout: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: '#f2f2f2',
  },
  // Top Header Bar - Full Width
  topHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 24px',
    backgroundColor: '#0a1b45',
    boxShadow: '0 2px 8px rgba(10, 27, 69, 0.15)',
    zIndex: 100,
    flexShrink: 0,
  },
  topHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  topHeaderLogo: {
    width: '32px',
    height: '32px',
    objectFit: 'contain',
  },
  topHeaderTitleGroup: {
    display: 'flex',
    flexDirection: 'column',
  },
  topHeaderTitle: {
    color: '#ffffff',
    fontSize: '16px',
    fontWeight: '500',
  },
  topHeaderSubtitle: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: '12px',
  },
  topHeaderRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  topHeaderUserAvatar: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '13px',
    fontWeight: '500',
  },
  topHeaderUserName: {
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: '400',
  },
  topHeaderSignOutButton: {
    color: 'rgba(255, 255, 255, 0.8)',
    minWidth: 'auto',
  },
  topHeaderSettingsButton: {
    color: 'rgba(255, 255, 255, 0.8)',
    minWidth: 'auto',
  },
  // Main Container (Sidebar + Content)
  mainContainer: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
    backgroundColor: '#f2f2f2',
  },
  // Left Sidebar
  sidebar: {
    width: '240px',
    backgroundColor: '#ffffff',
    borderRight: '1px solid #e0e0e0',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    transition: 'width 0.2s ease',
    overflow: 'hidden',
  },
  sidebarCollapsed: {
    width: '48px',
  },
  collapseButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: '14px 12px',
    cursor: 'pointer',
    backgroundColor: 'transparent',
    border: 'none',
    color: '#424242',
    ':hover': {
      backgroundColor: '#f5f5f5',
    },
  },
  collapseButtonCollapsed: {
    justifyContent: 'center',
    padding: '14px',
  },
  sidebarHeader: {
    padding: '16px',
    borderBottom: '1px solid #e8e8e8',
  },
  sidebarBrand: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  sidebarLogo: {
    width: '28px',
    height: '28px',
    objectFit: 'contain',
  },
  sidebarBrandText: {
    display: 'flex',
    flexDirection: 'column',
  },
  sidebarTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#0a1b45',
    lineHeight: '1.2',
  },
  sidebarSubtitle: {
    fontSize: '11px',
    color: '#666',
    lineHeight: '1.2',
  },
  sidebarNav: {
    padding: '8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 12px',
    borderRadius: '6px',
    cursor: 'pointer',
    color: '#424242',
    fontSize: '14px',
    fontWeight: '400',
    transition: 'all 0.15s ease',
    border: 'none',
    backgroundColor: 'transparent',
    width: '100%',
    textAlign: 'left',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    ':hover': {
      backgroundColor: '#f5f5f5',
    },
  },
  navItemCollapsed: {
    justifyContent: 'center',
    padding: '10px',
    gap: '0',
  },
  navItemText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  navItemHidden: {
    display: 'none',
  },
  navItemActive: {
    backgroundColor: '#e8f4fd',
    color: '#0078d4',
    fontWeight: '500',
  },
  navItemIcon: {
    width: '20px',
    height: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  navItemExpander: {
    marginLeft: 'auto',
    opacity: 0.5,
  },
  navSection: {
    marginTop: '8px',
    paddingTop: '8px',
    borderTop: '1px solid #e8e8e8',
  },
  navSectionTitle: {
    padding: '8px 12px 4px',
    fontSize: '11px',
    fontWeight: '600',
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  viewModeSection: {
    padding: '8px',
    marginTop: '8px',
  },
  viewModeButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 12px',
    borderRadius: '6px',
    cursor: 'pointer',
    color: '#424242',
    fontSize: '13px',
    fontWeight: '400',
    transition: 'all 0.15s ease',
    border: '1px solid transparent',
    backgroundColor: 'transparent',
    width: '100%',
    textAlign: 'left',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    ':hover': {
      backgroundColor: '#f5f5f5',
    },
  },
  viewModeButtonCollapsed: {
    justifyContent: 'center',
    padding: '8px',
    gap: '0',
  },
  viewModeButtonActive: {
    backgroundColor: '#0078d4',
    color: '#ffffff',
    fontWeight: '500',
    ':hover': {
      backgroundColor: '#106ebe',
    },
  },
  sidebarFooter: {
    marginTop: 'auto',
    padding: '12px',
    borderTop: '1px solid #e8e8e8',
  },
  userSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px',
  },
  userAvatar: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: '#0078d4',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: '500',
  },
  userName: {
    flex: 1,
    fontSize: '13px',
    fontWeight: '500',
    color: '#424242',
  },
  signOutButton: {
    minWidth: 'auto',
    padding: '6px',
    color: '#666',
  },
  // Main Content Area
  mainContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    backgroundColor: '#f2f2f2',
  },
});

interface AppLayoutProps {
  children: React.ReactNode;
  userName?: string;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  onLogout: () => void;
  currentPage: 'home' | 'chat';
  onNavigateHome?: () => void;
  areaName?: string;
}

export const AppLayout: React.FC<AppLayoutProps> = ({
  children,
  userName,
  viewMode,
  onViewModeChange,
  onLogout,
  currentPage,
  onNavigateHome,
  areaName,
}) => {
  const styles = useStyles();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showAdminSettings, setShowAdminSettings] = useState(false);

  const getInitials = (name?: string) => {
    if (!name) return '?';
    const parts = name.split(' ');
    return parts.length > 1 
      ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
      : name[0].toUpperCase();
  };

  return (
    <div className={styles.layout}>
      {/* Top Header - Full Width */}
      <div className={styles.topHeader}>
        <div className={styles.topHeaderLeft}>
          <img 
            src="/dynamics-logo.png" 
            alt="Dynamics 365" 
            className={styles.topHeaderLogo}
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
          <div className={styles.topHeaderTitleGroup}>
            <div className={styles.topHeaderTitle}>ERP Agent Prompt Library</div>
            <div className={styles.topHeaderSubtitle}>For Dynamics 365 Finance and Operations</div>
          </div>
        </div>
        <div className={styles.topHeaderRight}>
          <div className={styles.topHeaderUserAvatar}>{getInitials(userName)}</div>
          <span className={styles.topHeaderUserName}>{userName}</span>
          <Tooltip content="Agent Settings" relationship="label">
            <Button
              appearance="subtle"
              size="small"
              icon={<Settings24Regular />}
              className={styles.topHeaderSettingsButton}
              onClick={() => setShowAdminSettings(true)}
              title="Agent Settings"
            />
          </Tooltip>
          <Button
            appearance="subtle"
            size="small"
            icon={<SignOut24Regular />}
            className={styles.topHeaderSignOutButton}
            onClick={onLogout}
            title="Sign out"
          />
        </div>

      {/* Agent Settings Modal */}
      <AdminSettingsModal
        open={showAdminSettings}
        onClose={() => setShowAdminSettings(false)}
      />
      </div>

      {/* Body Container - Sidebar + Content */}
      <div className={styles.mainContainer}>
        {/* Left Sidebar */}
        <div className={mergeClasses(styles.sidebar, isCollapsed && styles.sidebarCollapsed)}>
          {/* Collapse Toggle Button */}
          <button
            className={mergeClasses(styles.collapseButton, isCollapsed && styles.collapseButtonCollapsed)}
            onClick={() => setIsCollapsed(!isCollapsed)}
            title={isCollapsed ? 'Expand menu' : 'Collapse menu'}
          >
            <LineHorizontal320Regular />
          </button>

          {/* Navigation */}
          <div className={styles.sidebarNav}>
            <Tooltip content="Home" relationship="label" positioning="after" visible={isCollapsed ? undefined : false}>
              <button
                className={mergeClasses(
                  styles.navItem,
                  currentPage === 'home' && styles.navItemActive,
                  isCollapsed && styles.navItemCollapsed
                )}
                onClick={onNavigateHome}
              >
                <span className={styles.navItemIcon}>
                  {currentPage === 'home' ? <Home24Filled /> : <Home24Regular />}
                </span>
                <span className={isCollapsed ? styles.navItemHidden : styles.navItemText}>Home</span>
              </button>
            </Tooltip>
            
            <Tooltip content="Recent" relationship="label" positioning="after" visible={isCollapsed ? undefined : false}>
              <button className={mergeClasses(styles.navItem, isCollapsed && styles.navItemCollapsed)}>
                <span className={styles.navItemIcon}><Clock24Regular /></span>
                <span className={isCollapsed ? styles.navItemHidden : styles.navItemText}>Recent</span>
                {!isCollapsed && <ChevronDown20Regular className={styles.navItemExpander} />}
              </button>
            </Tooltip>
            
            <Tooltip content="Pinned" relationship="label" positioning="after" visible={isCollapsed ? undefined : false}>
              <button className={mergeClasses(styles.navItem, isCollapsed && styles.navItemCollapsed)}>
                <span className={styles.navItemIcon}><Pin24Regular /></span>
                <span className={isCollapsed ? styles.navItemHidden : styles.navItemText}>Pinned</span>
                {!isCollapsed && <ChevronUp20Regular className={styles.navItemExpander} />}
              </button>
            </Tooltip>

            {/* Home Section */}
            {!isCollapsed && (
              <div className={styles.navSection}>
                <div className={styles.navSectionTitle}>Home</div>
                <button className={styles.navItem}>
                  <span className={styles.navItemIcon}><Navigation24Regular /></span>
                  <span className={styles.navItemText}>Get started</span>
                </button>
              </div>
            )}
            {isCollapsed && (
              <Tooltip content="Get started" relationship="label" positioning="after">
                <button className={mergeClasses(styles.navItem, styles.navItemCollapsed)}>
                  <span className={styles.navItemIcon}><Navigation24Regular /></span>
                </button>
              </Tooltip>
            )}

            {/* View Mode Toggle */}
            <div className={styles.viewModeSection}>
              <Tooltip content="By Role" relationship="label" positioning="after" visible={isCollapsed ? undefined : false}>
                <button
                  className={mergeClasses(
                    styles.viewModeButton,
                    viewMode === 'role-based' && styles.viewModeButtonActive,
                    isCollapsed && styles.viewModeButtonCollapsed
                  )}
                  onClick={() => onViewModeChange('role-based')}
                >
                  <People24Regular />
                  <span className={isCollapsed ? styles.navItemHidden : undefined}>By Role</span>
                </button>
              </Tooltip>
              <Tooltip content="By Process" relationship="label" positioning="after" visible={isCollapsed ? undefined : false}>
                <button
                  className={mergeClasses(
                    styles.viewModeButton,
                    viewMode === 'business-process' && styles.viewModeButtonActive,
                    isCollapsed && styles.viewModeButtonCollapsed
                  )}
                  onClick={() => onViewModeChange('business-process')}
                >
                  <Flow24Regular />
                  <span className={isCollapsed ? styles.navItemHidden : undefined}>By Process</span>
                </button>
              </Tooltip>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className={styles.mainContent}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default AppLayout;






